import React from 'react';
import PropTypes from 'prop-types';
import styled, { keyframes } from 'styled-components';

const shimmer = keyframes`
  0% { background-position: -400px 0; }
  100% { background-position: 400px 0; }
`;

const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const Bone = styled.span`
  display: block;
  width: ${({ $w }) => $w || '100%'};
  height: ${({ $h }) => $h || '12px'};
  flex-shrink: 0;
  border-radius: ${({ $r }) => $r || '6px'};
  background: linear-gradient(
    90deg,
    var(--color-bg-tertiary) 0%,
    var(--color-hover-overlay) 50%,
    var(--color-bg-tertiary) 100%
  );
  background-size: 800px 100%;
  animation: ${shimmer} 1.4s ease-in-out infinite;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

const Row = styled.span`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
`;

const CardShell = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.625rem;
  min-width: 0;
  padding: 0.875rem 0.875rem 0.625rem;
  border: 1px solid var(--color-border-primary);
  border-radius: 10px;
  background: var(--color-bg-primary);
  box-sizing: border-box;
  animation: ${fadeIn} 0.2s ease both;
  animation-delay: ${({ $index }) => `${Math.min($index, 8) * 30}ms`};

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

const CardFooter = styled(Row)`
  padding-top: 0.5rem;
  border-top: 1px solid var(--color-border-primary);
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
  gap: 0.75rem;
  margin-top: 1.25rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const Board = styled.div`
  display: flex;
  gap: 0.75rem;
  margin-top: 1rem;
  height: calc(100vh - 300px);
  min-height: 420px;
  overflow: hidden;
`;

const ColumnShell = styled.div`
  flex: 0 0 296px;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  padding: 0.75rem 0.5rem;
  border: 1px solid var(--color-border-primary);
  border-radius: 12px;
  background: var(--color-bg-secondary);
  box-sizing: border-box;
  overflow: hidden;
`;

const SkeletonCard = ({ index = 0 }) => (
  <CardShell $index={index}>
    <Row>
      <Bone $w="45%" $h="10px" />
      <Bone $w="48px" $h="18px" $r="999px" />
    </Row>
    <Bone $w="92%" $h="13px" />
    <Bone $w="64%" $h="13px" />
    <Bone $w="72px" $h="10px" />
    <CardFooter>
      <Bone $w="22px" $h="22px" $r="50%" />
      <Bone $w="56px" $h="10px" />
    </CardFooter>
  </CardShell>
);

SkeletonCard.propTypes = {
  index: PropTypes.number,
};

export const TicketCardsSkeleton = ({ count = 8 }) => (
  <Grid aria-busy="true" aria-label="Carregando tickets">
    {Array.from({ length: count }, (_, index) => (
      <SkeletonCard key={index} index={index} />
    ))}
  </Grid>
);

TicketCardsSkeleton.propTypes = {
  count: PropTypes.number,
};

const KANBAN_SKELETON_CARDS = [3, 2, 4, 1, 2];

export const TicketKanbanSkeleton = () => (
  <Board aria-busy="true" aria-label="Carregando quadro de tickets">
    {KANBAN_SKELETON_CARDS.map((cards, column) => (
      <ColumnShell key={column}>
        <Row style={{ padding: '0 0.375rem 0.25rem' }}>
          <Bone $w="45%" $h="12px" />
          <Bone $w="20px" $h="12px" />
        </Row>
        {Array.from({ length: cards }, (_, index) => (
          <SkeletonCard key={index} index={column + index} />
        ))}
      </ColumnShell>
    ))}
  </Board>
);
